'use client'

import React from 'react'
import { ArrowUp, ArrowUpDown, Check, Delete, ExternalLink } from 'lucide-react'
import { Drawer } from 'vaul'
import { cn } from '@/lib/utils'
import { ESPO_URL } from '@/utils/constants'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import {
	bestQuoteExactOut,
	SFAMM_VIA_ROUTER,
	BTC_ID,
	formatAmount,
	FRBTC_ID,
	parseAmount,
	sideLabels,
	type PoolToken
} from '../lib/amm'
import {
	btcSideSats,
	carryAcrossFlip,
	convertEntry,
	entryUnits,
	groupWhole,
	nextUnit,
	pctEntry,
	prefillFor,
	press,
	toAmountString,
	typedNumber,
	unitDecimals,
	type EntryUnit
} from '../lib/entry'
import { buyFunding } from '../lib/funding'
import { useAlkanes } from '../lib/store'
import { Change, McChip } from './asset-row'
import SwapPanel, { type BroadcastInfo } from './swap-panel'
import { TokenIcon } from './token-select'

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫']

/// The giant amount, fomo behavior: the font SHRINKS as digits grow so the
/// entered value is always fully visible — truncating the money figure showed
/// the user a different number than the one they typed. Measured, not
/// guessed: render at 56px, compare content width to the container, scale
/// down proportionally. Floor 16px: `press` caps ENTRY at 9 integer digits,
/// but a ⇅ flip carries the value across units (9-digit BTC becomes 18-digit
/// dollars), and at 16px even that widest legal figure fits 320px whole.
function FitAmount({ children }: { children: React.ReactNode }) {
	const ref = React.useRef<HTMLParagraphElement>(null)
	React.useLayoutEffect(() => {
		const el = ref.current
		if (!el) {
			return
		}
		const fit = () => {
			// Proportional scale, then iterate: fixed-margin children make the
			// first ratio an estimate, so converge instead of trusting it.
			let size = 56
			el.style.fontSize = `${size}px`
			for (let i = 0; i < 6 && el.scrollWidth > el.clientWidth && size > 16; i++) {
				size = Math.max(16, Math.floor((size * el.clientWidth) / el.scrollWidth))
				el.style.fontSize = `${size}px`
			}
		}
		fit()
		const ro = new ResizeObserver(fit)
		ro.observe(el)
		return () => ro.disconnect()
	})
	return (
		<p
			ref={ref}
			data-testid="alkanes-sheet-amount"
			// `block`: the site's global p{display:inline-block} would otherwise
			// seat the converter beside the amount. nowrap + hidden give the
			// measurer a real overflow to read; the effect above removes it.
			// The size lives in the STYLE attribute, not a `text-[56px]` class:
			// tailwind.config sets `important: true`, so the utility would beat
			// the fitted inline font-size and re-truncate the figure.
			style={{ fontSize: '56px' }}
			className="block overflow-hidden whitespace-nowrap font-bold leading-none tabular-nums"
		>
			{children}
		</p>
	)
}

/// The buy/sell sheet — fomo's numpad screen, wired to this app's audited
/// machinery, which now renders INSIDE it: as the user types, the inline
/// invoice materializes beneath the amount (route, funding, rate, impact,
/// fees, minimum received), and the CTA advances the SAME sheet to the frozen
/// confirm stage. There is no separate swap module anymore — but the road to
/// a signature is unchanged: the panel's quote, guards, frozen review and
/// real-PSBT fee run exactly as before, and nothing signs on a first click.
///
/// Entry is BTC-first on buys (the typed figure IS the sell-side amount,
/// sats-exact) and token-first on sells (you sell what you hold, and the 100%
/// chip must sell the exact held amount). The ⇅ cycles the other units.
export default function TradeSheet({
	side,
	onClose
}: {
	side: 'buy' | 'sell'
	onClose: () => void
}) {
	const asset = useAlkanes((s) => s.asset)
	const btcUsd = useAlkanes((s) => s.btcUsd)
	const balances = useAlkanes((s) => s.balances)
	const balancesUnavailable = useAlkanes((s) => s.balancesUnavailable)
	const tokens = useAlkanes((s) => s.tokens)
	const pools = useAlkanes((s) => s.pools)
	const routerFeePer1000 = useAlkanes((s) => s.routerFeePer1000)
	const sfammPools = useAlkanes((s) => s.sfammPools)
	const routeOpts = React.useMemo(
		() => ({ sfammPools, sfammViaRouter: SFAMM_VIA_ROUTER }),
		[sfammPools]
	)
	const selectSell = useAlkanes((s) => s.selectSell)
	const selectBuy = useAlkanes((s) => s.selectBuy)
	const watchAddress = useAlkanes((s) => s.watchAddress)
	const wrapSwapTopup = useAlkanes((s) => s.wrapSwapTopup)
	const loggedIn = useLoggedIn()
	// Reads behave logged-in under watch mode; only signing stays gated.
	const canRead = Boolean(loggedIn) || watchAddress !== null
	const balancesReady = canRead && !balancesUnavailable

	const [done, setDone] = React.useState<BroadcastInfo | null>(null)
	const [reviewOpen, setReviewOpen] = React.useState(false)
	const [raw, setRaw] = React.useState('')

	// BTC entry is the default on buys, the token itself on sells; the ⇅
	// cycles whatever units the live rates can honestly convert. Derived, not
	// initialised: the BTC price can land a beat after the sheet mounts, and a
	// default frozen at mount would strand the session in the wrong unit.
	const price = asset?.price_usd ?? null
	const ctx = React.useMemo(() => ({ btcUsd, price }), [btcUsd, price])
	const units = entryUnits(side, ctx)
	const [unitChoice, setUnitChoice] = React.useState<EntryUnit | null>(null)
	const unit: EntryUnit = unitChoice !== null && units.includes(unitChoice) ? unitChoice : units[0]

	const sheetRef = React.useRef<HTMLDivElement>(null)

	// The pair is set explicitly on open. Buys are frBTC-FIRST: held frBTC is
	// the working balance and pays whenever it covers the buy (a plain swap —
	// no wrap fee, one transaction); native BTC is the top-up valve behind it.
	// Sells stop at frBTC, which is 1:1 with BTC — the proceeds READ in BTC and
	// stay spendable as the next buy's funding. frBTC's own screen keeps the
	// signer wrap/unwrap doors instead (it cannot trade against itself).
	const assetToken = React.useMemo(
		() => (asset ? tokens.find((t) => t.id === asset.id) ?? null : null),
		[tokens, asset]
	)
	const btcToken = tokens[0] ?? null
	const frbtcToken = React.useMemo(
		(): PoolToken =>
			tokens.find((t) => t.id === FRBTC_ID) ?? { id: FRBTC_ID, name: 'frBTC', icon: null },
		[tokens]
	)

	const typed = typedNumber(raw)

	// The BTC-side input this buy needs, for the funding decision. BTC entry
	// IS the input; USD converts at the live price; token entry inverts the
	// live route the same way the panel's exact-out quote will.
	const neededIn: bigint | null = React.useMemo(() => {
		if (side !== 'buy' || typed <= 0 || !asset) {
			return null
		}
		if (unit !== 'token') {
			return btcSideSats(unit, raw, ctx)
		}
		const want = parseAmount(toAmountString(typed, 8))
		if (!want || want <= 0n) {
			return null
		}
		const q = bestQuoteExactOut(pools, FRBTC_ID, asset.id, want, routerFeePer1000, routeOpts)
		return q ? q.amountIn : null
	}, [side, typed, unit, raw, ctx, pools, routerFeePer1000, routeOpts, asset])

	const assetIsFrbtc = asset?.id === FRBTC_ID
	const frbtcBal = assetIsFrbtc ? 0n : balances.get(FRBTC_ID) ?? 0n
	// Third rung admitted only while the API capability flag holds — the
	// panel verifies it on the first real build and downgrades the session.
	const funding = buyFunding(frbtcBal, balances.get(BTC_ID) ?? 0n, neededIn, wrapSwapTopup)

	React.useEffect(() => {
		if (!assetToken || !btcToken) {
			return
		}
		if (side === 'buy') {
			selectSell(funding.source === 'frbtc' ? frbtcToken : btcToken)
			selectBuy(assetToken)
		} else {
			selectSell(assetToken)
			selectBuy(assetIsFrbtc ? btcToken : frbtcToken)
		}
	}, [side, assetToken, btcToken, frbtcToken, assetIsFrbtc, funding.source, selectSell, selectBuy])

	// Focus lands on the sheet, Escape leaves it, and the page behind it does
	// not scroll while it is up.
	React.useEffect(() => {
		sheetRef.current?.focus({ preventScroll: true })
		const prev = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = prev
		}
	}, [])

	if (!asset) {
		return null
	}

	const symbol = asset.name || asset.id
	const labels = sideLabels(asset.id)
	const verb = side === 'buy' ? labels.Buy : labels.Sell

	// What the entry hands the audited panel, live on every keystroke. BTC
	// entry passes through sats-exact; USD converts at the live price; token
	// entry is exact-out. Sells always hand over the token sell side.
	const prefill = prefillFor(side, unit, raw, ctx)

	// 25/50/75/100% of what is actually spendable, computed in bigint so 100%
	// is the exact balance: the funding ladder's spendable for buys, the held
	// token for sells. The chip fills the input — the quote, guards and review
	// run exactly as for a typed amount.
	const spendableRaw = side === 'buy' ? funding.spendable : balances.get(asset.id) ?? 0n
	const setPct = (pct: number) => {
		const next = pctEntry(side, unit, spendableRaw, pct, ctx)
		if (next !== '') {
			setRaw(next)
		}
	}

	const flipUnit = () => {
		if (units.length < 2) {
			return
		}
		const to = nextUnit(unit, side, ctx)
		// Carry the value across the flip so the number on screen stays the
		// same quantity, just in the other unit.
		setRaw((r) => carryAcrossFlip(r, unit, to, ctx))
		setUnitChoice(to)
	}

	const pressKey = (k: string) => {
		setRaw((r) => press(r, k, unitDecimals(unit)))
	}

	// The converter line: the same amount in the next unit of the ⇅ cycle, so
	// the flip is predictable. Estimates wear ≈; the invoice below carries the
	// real quote.
	const converterUnit = units.length > 1 ? nextUnit(unit, side, ctx) : null
	const converted = converterUnit !== null ? convertEntry(typed, unit, converterUnit, ctx) : null
	const converterText =
		converterUnit === null || converted === null
			? '—'
			: converterUnit === 'usd'
			? `≈ $${groupWhole(toAmountString(converted, 2) || '0')}`
			: converterUnit === 'btc'
			? `≈ ${toAmountString(converted, 8) || '0'} BTC`
			: `≈ ${groupWhole(toAmountString(converted, converted >= 1 ? 2 : 6) || '0')} ${symbol}`

	// The USD the user was thinking in, for the completion headline.
	const usdValue = convertEntry(typed, unit, 'usd', ctx)

	const heading = done
		? 'Trade complete'
		: reviewOpen
		? `Review ${verb.toLowerCase()}`
		: `${verb} ${symbol}`

	return (
		// vaul: a transform-driven bottom sheet with real touch gestures — drag
		// the handle (or the sheet) down to dismiss, velocity-aware, body
		// scroll locked, safe on iOS. Sized to its content and bottom-anchored
		// (fomo's shape); its height only changes on the user's own tap between
		// the two stages — never while typing, because the entry stage carries
		// no invoice (see SwapPanel `staged`).
		<Drawer.Root
			open
			onOpenChange={(open) => {
				if (!open) onClose()
			}}
			shouldScaleBackground={false}
		>
			<Drawer.Portal>
				<Drawer.Overlay className="alkanes-ink fixed inset-0 z-50 bg-black/70" />
				<Drawer.Content
					ref={sheetRef}
					aria-label={heading}
					data-testid="alkanes-trade-sheet"
					className="alkanes-ink fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[92dvh] w-full max-w-[440px] flex-col rounded-t-3xl bg-background outline-none"
				>
					<Drawer.Title className="sr-only">{heading}</Drawer.Title>
					<Drawer.Description className="sr-only">
						Enter an amount on the numpad, then confirm the trade.
					</Drawer.Description>
					{/* Grabber — vaul's handle, so a tap on it also dismisses. */}
					<Drawer.Handle className="mb-1 mt-2.5 !h-1 !w-9 !bg-muted-foreground/40" />
					<div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">

				{done ? (
					<DoneStage info={done} side={side} usd={usdValue} onClose={onClose} />
				) : (
					<React.Fragment>
						{/* Token header row — icon, symbol, MC chip; price + 24h right. */}
						<div className="flex items-center gap-2.5 px-4 pt-2">
							<TokenIcon token={{ id: asset.id, name: asset.name, icon: asset.icon }} size={34} />
							<div className="min-w-0 flex-1">
								{/* `block`: the site's global p{display:inline-block} would seat
								    the MC chip beside the symbol instead of under it. */}
								<p className="block truncate text-[15px] font-bold leading-tight">{symbol}</p>
								<McChip mc={asset.market_cap_usd} className="mt-0.5" />
							</div>
							<div className="flex shrink-0 flex-col items-end gap-0.5">
								<span className="text-[15px] font-semibold tabular-nums leading-tight">
									{asset.price_usd !== null ? (
										`$${
											asset.price_usd >= 1
												? asset.price_usd.toLocaleString('en-US', { maximumFractionDigits: 2 })
												: asset.price_usd.toPrecision(4)
										}`
									) : (
										<span className="text-muted-foreground">—</span>
									)}
								</span>
								<Change v={asset.change_24h_pct} className="text-xs font-medium" />
							</div>
						</div>

						{/* The giant amount — fit to width, never truncated. */}
						<div className="mt-4 px-4 text-center">
							<FitAmount>
								{unit === 'usd' ? `$${groupWhole(raw || '0')}` : groupWhole(raw || '0')}
								{unit !== 'usd' && (
									// em-sized so the unit shrinks WITH the fitted figure
									// (a fixed 24px label would break the fit math).
									<span className="ml-2 text-[0.43em] font-semibold text-muted-foreground">
										{unit === 'btc' ? 'BTC' : symbol}
									</span>
								)}
							</FitAmount>
							{units.length > 1 ? (
								<button
									type="button"
									data-testid="alkanes-sheet-flip"
									onClick={flipUnit}
									disabled={reviewOpen}
									className="alkanes-touch mt-2.5 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm tabular-nums text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
								>
									{converterText}
									<ArrowUpDown className="size-3.5" aria-hidden="true" />
									<span className="sr-only">
										Switch to entering the amount in{' '}
										{converterUnit === 'usd' ? 'dollars' : converterUnit === 'btc' ? 'BTC' : symbol}
									</span>
								</button>
							) : (
								<p className="mt-2.5 text-sm text-muted-foreground">
									No live price — amounts enter in {unit === 'btc' ? 'BTC' : symbol}
								</p>
							)}
						</div>

						{/* Entry chrome — collapses while the frozen review is up. */}
						<div
							className={cn('alkanes-reveal', !reviewOpen && 'alkanes-reveal-open')}
							aria-hidden={reviewOpen}
						>
							<div className="min-h-0 overflow-hidden">
								{/* % chips of the spendable balance. */}
								<div className="mt-2 flex justify-center gap-2 px-4">
									{[25, 50, 75, 100].map((pct) => (
										<button
											key={pct}
											type="button"
											data-testid={`alkanes-sheet-pct-${pct}`}
											onClick={() => setPct(pct)}
											disabled={reviewOpen || !balancesReady || spendableRaw <= 0n}
											className="alkanes-touch h-10 flex-1 max-w-[88px] rounded-xl bg-secondary text-sm font-semibold tabular-nums transition-colors hover:bg-secondary/70 active:bg-muted disabled:opacity-40"
										>
											{pct}%
										</button>
									))}
								</div>

								{/* The numpad — real buttons, real focus order. */}
								<div className="mt-2 grid grid-cols-3 px-2 pb-1" data-testid="alkanes-numpad">
									{KEYS.map((k) => (
										<button
											key={k}
											type="button"
											data-testid={`alkanes-key-${k === '⌫' ? 'del' : k}`}
											aria-label={k === '⌫' ? 'Delete last digit' : k}
											disabled={reviewOpen}
											onClick={() => pressKey(k)}
											className="grid h-12 place-items-center rounded-xl text-2xl font-medium tabular-nums transition-colors hover:bg-secondary/50 active:bg-secondary"
										>
											{k === '⌫' ? <Delete className="size-6" aria-hidden="true" /> : k}
										</button>
									))}
								</div>
							</div>
						</div>

						{/* The audited machinery, rendered as this sheet's own invoice
						    and confirm stage: quote, funding ladder, frozen review,
						    real-PSBT fee — same engine, same guards, no module switch. */}
						<div className="px-4 pt-1">
							<SwapPanel
								staged
								sellAmount={prefill?.sell}
								buyAmount={prefill?.buy}
								reviewLabel={verb}
								// The CTA is pinned to the sheet's foot: it does not move
								// when the invoice reveals, when the quote lands, or when
								// a short viewport makes the entry scroll.
								footClassName="alkanes-sheet-foot -mx-4 mt-2 bg-background px-4 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-1"
								onReviewChange={setReviewOpen}
								onBroadcast={setDone}
							/>
						</div>
					</React.Fragment>
				)}
					</div>
				</Drawer.Content>
			</Drawer.Portal>
		</Drawer.Root>
	)
}

/// The completion screen, fomo's withdraw-complete anatomy: the green circle,
/// the verb and amount, the short txid, the explorer link, and the green bar
/// that closes the sheet. Rendered only AFTER a real broadcast — everything on
/// it comes from the transaction that just went out.
function DoneStage({
	info,
	side,
	usd,
	onClose
}: {
	info: BroadcastInfo
	side: 'buy' | 'sell'
	/// The typed amount's USD equivalent at entry time, when the rates could
	/// say — shown as the headline because value is the universal language of
	/// a completed trade.
	usd: number | null
	onClose: () => void
}) {
	const verb =
		info.kind === 'wrap'
			? 'Wrapped'
			: info.kind === 'unwrap'
			? 'Unwrapping'
			: side === 'buy'
			? 'Bought'
			: 'Sold'
	const headline =
		usd !== null && usd > 0
			? `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
			: `${formatAmount(BigInt(info.amountOutRaw), 4)} ${info.outName}`
	return (
		<div
			className="flex flex-col items-center px-6 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-10 text-center"
			data-testid="alkanes-trade-done"
		>
			<span className="grid size-20 place-items-center rounded-full bg-success">
				<ArrowUp
					className={cn('size-9 text-success-foreground', side === 'buy' && 'rotate-180')}
					aria-hidden="true"
				/>
			</span>
			<h2 className="mt-6 text-2xl font-semibold">{verb}</h2>
			<p className="mt-1 text-4xl font-bold tabular-nums">{headline}</p>
			{/* A swap's fill can land anywhere above min_out, so its output is
			    quoted with ≈; wrap and unwrap amounts are deterministic. */}
			<p className="mt-2 text-sm text-muted-foreground">
				{formatAmount(BigInt(info.amountInRaw), 4)} {info.sellName} →{' '}
				{info.kind === 'swap' ? '≈ ' : ''}
				{formatAmount(BigInt(info.amountOutRaw), 4)} {info.outName}
			</p>
			{info.kind === 'swap' && info.outId === FRBTC_ID && (
				<p className="mt-1 text-xxs leading-relaxed text-muted-foreground">
					Received as frBTC — 1 frBTC = 1 BTC, spendable on your next buy.
				</p>
			)}
			<p className="mt-5 font-mono text-sm tabular-nums text-muted-foreground">
				{info.txid.slice(0, 8)}…{info.txid.slice(-6)}
			</p>
			<div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-semibold text-primary">
				<a
					data-testid="alkanes-trade-espo-link"
					href={`${ESPO_URL}/tx/${info.txid}`}
					target="_blank"
					rel="noreferrer"
					className="alkanes-touch inline-flex items-center gap-1.5 hover:underline"
				>
					View on espo.sh
					<ExternalLink className="size-4" aria-hidden="true" />
				</a>
			</div>
			{info.kind === 'unwrap' && (
				<p className="mt-3 max-w-[36ch] text-xxs leading-relaxed text-muted-foreground">
					The SUBFROST federation settles unwraps asynchronously — BTC arrives after it processes
					this transaction.
				</p>
			)}
			<button
				type="button"
				data-testid="alkanes-trade-done-close"
				onClick={onClose}
				className="mt-10 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-base font-bold text-primary-foreground transition-colors hover:bg-primary/90"
			>
				{verb === 'Unwrapping' ? 'Unwrap submitted' : `${verb} — done`}
				<Check className="size-5" aria-hidden="true" />
			</button>
		</div>
	)
}
